import { useContext } from "react";
import Card from "./Card";
import ProgressCircle from "../Elements/ProgressCircle";
import { useLanguage } from "../../context/LanguageContext";
import { YearContext, AuthContext } from "../../context/context";
import dayjs from "dayjs";

const CardProgress = ({ cardClassName, useMaxHeight = true }) => {
  const { copy } = useLanguage();
  const { userData } = useContext(AuthContext);
  const { selectedYear } = useContext(YearContext);

  const getYearlyMilestones = () => {
    if (!Array.isArray(userData.milestones)) {
      console.error("Milestones should be an array", userData.milestones);
      return { total: 0, completed: 0 };
    }

    const allMilestones = userData.milestones.filter((milestone) => {
      const goal = userData.goals.find((g) => g.id === milestone.goal_id);
      return goal && goal.year === selectedYear;
    });

    const completed = allMilestones.filter(
      (milestone) =>
        milestone.status === true || milestone.status === "completed"
    ).length;

    return { total: allMilestones.length, completed };
  };

  const { total, completed } = getYearlyMilestones();

  const calculateYearlyProgress = () => {
    return total > 0 ? completed / total : 0;
  };

  // Calculate streak: consecutive days with at least one milestone statusChangedAt
  const calculateMilestoneStreak = (milestones) => {
    if (!Array.isArray(milestones)) return 0;

    // Get all unique dates where milestones were completed
    const daysWithChange = new Set(
      milestones
        .filter((m) => m.status && m.statusChangedAt)
        .map((m) => dayjs(m.statusChangedAt).format("YYYY-MM-DD"))
    );

    // Calculate streak
    let streak = 0;
    let current = dayjs();

    // Check if today has any completions
    if (!daysWithChange.has(current.format("YYYY-MM-DD"))) {
      // If no completions today, start from yesterday
      current = current.subtract(1, "day");
    }

    // Count consecutive days with completions
    while (daysWithChange.has(current.format("YYYY-MM-DD"))) {
      streak++;
      current = current.subtract(1, "day");
    }

    return streak;
  };

  const streak = calculateMilestoneStreak(userData.milestones || []);

  return (
    <Card
      title={`Progress ${selectedYear}`}
      description={copy.card_progress.description}
      useMaxHeight={useMaxHeight}
      cardClassName={cardClassName || "h-full"}

    >
      <div className="flex flex-col gap-5 h-full items-center justify-between">
        <div className="flex-[3]">
          <ProgressCircle value={calculateYearlyProgress()} />
        </div>
        <div className="w-full flex-[1] grid grid-cols-3 md:grid-cols-2 lg:grid-cols-3 text-base font-medium mt-4 gap-2 md:gap-1">
        <div className="flex flex-col items-center card-base-border rounded-md p-2 md:p-1 justify-center md:col-span-2 lg:col-span-1">
            <span className="text-2xl">{streak}</span>
            <span className="md:text-xs text-sm mt-1">
              {copy.card_progress.stats.streak}
            </span>
          </div>
          <div className="flex flex-col items-center card-base-border rounded-md p-2 md:p-1 justify-center">
            <span className="text-2xl">{completed}</span>
            <span className="md:text-xs text-sm mt-1">
              {copy.card_progress.stats.completed}
            </span>
          </div>
       
          <div className="flex flex-col items-center card-base-border rounded-md p-2 md:p-1 justify-center">
            <span className="text-2xl">{total}</span>
            <span className="md:text-xs text-sm mt-1">
              {copy.card_progress.stats.total}
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default CardProgress;
